'use client';

import React from 'react';
import Image from 'next/image';
import { useSignedUrl } from '@/hooks/use-optimized-signed-urls';
import { Skeleton } from '@/components/ui/skeleton';
import { usePreloadedImages } from '@/contexts/PreloadedImageContext';

interface SignedItemThumbnailProps {
  blobPathname?: string | null;
  altText: string;
  className?: string;
  preloadedUrl?: string;
  isWaitingForBatch?: boolean;
}

const SignedItemThumbnail: React.FC<SignedItemThumbnailProps> = (props) => {
  const { blobPathname, altText, className, preloadedUrl, isWaitingForBatch = false } = props;
  if (!blobPathname) {
    return null; // Or a default placeholder if desired
  }

  // Get preloaded images from context
  const preloadedImages = usePreloadedImages();
  
  // Check for preloaded URL with proper key resolution
  let finalPreloadedUrl = preloadedUrl;
  if (!finalPreloadedUrl && blobPathname && preloadedImages) {
    // Try direct lookup first
    finalPreloadedUrl = preloadedImages[blobPathname];
    
    // If not found, try extracting pathname from blob URL
    if (!finalPreloadedUrl && blobPathname.includes('blob.vercel-storage.com/')) {
      const match = blobPathname.match(/blob\.vercel-storage\.com\/(.+)$/);
      if (match) {
        const pathname = match[1];
        finalPreloadedUrl = preloadedImages[pathname];
      }
    }
    
    // Also try as direct pathname (if blobPathname is already a pathname)
    if (!finalPreloadedUrl) {
      finalPreloadedUrl = preloadedImages[blobPathname];
    }
  }

  // If we have a preloaded URL, use it immediately without client-side fetch
  if (finalPreloadedUrl) {
    return (
      <Image
        src={finalPreloadedUrl}
        alt={altText}
        fill
        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 250px"
        className={`object-cover ${className || ''}`}
        priority={false} // Gallery images shouldn't block LCP
      />
    );
  }
  
  // Fallback to client-side fetch if no preloaded URL
  // Important: Don't fetch if preloadedUrl prop was passed (even if undefined) - it means parent is handling batch loading
  const wasPreloadedUrlPropPassed = 'preloadedUrl' in props;
  const { signedUrl, isLoading, error } = useSignedUrl(wasPreloadedUrlPropPassed ? null : blobPathname);

  // Show skeleton if we're waiting for batch results or individual fetch
  if (isLoading || (wasPreloadedUrlPropPassed && !finalPreloadedUrl && !signedUrl)) {
    return <Skeleton className={`w-full h-full ${className || ''}`} />;
  }

  const finalUrl = finalPreloadedUrl || signedUrl;
  
  if (error || !finalUrl) {
    // Render error placeholder that matches image dimensions
    return (
      <div 
        className={`bg-muted flex items-center justify-center w-full h-full ${className || ''}`} 
        title={typeof error === 'string' ? error : error?.message || 'Could not load image'}
      >
        <span className="text-2xl opacity-50">🖼️</span>
      </div>
    );
  }

  return (
    <Image
      src={finalUrl}
      alt={altText}
      fill
      sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 250px"
      className={`object-cover ${className || ''}`}
      priority={false} // Gallery images shouldn't block LCP
    />
  );
};

export default SignedItemThumbnail; 